import { FormDatepickerField, FormNumberField, FormSelect, FormTextField } from 'components/forms';
import { useContext } from 'react';
import { useForm, Validate } from 'react-hook-form';
import useSubmitNew from 'shared/hooks/useSubmitNew';
import MassActionModalLayout from 'shared/layouts/massActionModalLayout';
import { MassActionItemProps, UseMassActionProps } from 'shared/types';
import { isNotPastDate, isValidDate } from 'shared/utility/dataGrid';
import { MassActionForm } from 'types/backend';
import { InfoBar } from './ChangeByDollarAmount';

export type ChangeBySetValueActionForm = {
    value: string;
    start?: string;
    reason?: string;
    comments: string;
    priceType?: string;
};
export type ChangeBySetValueForm = MassActionForm<ChangeBySetValueActionForm>;
export type StartDateValidation = {
    startDateValidation?: Record<string, Validate<string>>;
    canSetStartDate?: boolean;
};

type ChangeBySetValueProps<T> = MassActionItemProps<ChangeBySetValueForm, T, ChangeBySetValueActionForm> &
    StartDateValidation &
    InfoBar;

export default function ChangeBySetValue<T>({
    defaultValues,
    onSubmit,
    isLoading,
    tableDataContext,
    rowCount,
    columnConfig,
    allRowsSelected,
    validate,
    startDateValidation,
    InfoBar,
    canSetStartDate,
}: ChangeBySetValueProps<T>) {
    const {
        handleSubmit,
        control,
        formState: { errors },
        watch,
    } = useForm<ChangeBySetValueForm>({
        defaultValues,
    });

    return (
        <MassActionModalLayout
            isLoading={isLoading}
            tableDataContext={tableDataContext}
            columnConfig={columnConfig}
            allRowsSelected={allRowsSelected}
            rowCount={rowCount}
            submitHandler={handleSubmit(onSubmit)}
        >
            <FormNumberField
                name={'actionForm.value'}
                label='Value'
                control={control}
                rules={{
                    required: 'Value is required',
                    validate,
                    maxLength: { value: 10, message: `Maximum 10 characters` },
                }}
                fieldError={errors?.['actionForm']?.['value']}
                type='money'
            />
            <FormDatepickerField
                name={'actionForm.start'}
                control={control}
                label='Start Date'
                rules={{
                    required: 'Start date is required',
                    validate: startDateValidation
                        ? { isValidDate, isNotPastDate, ...startDateValidation }
                        : { isValidDate, isNotPastDate },
                }}
                fieldError={errors?.['actionForm']?.['start']}
                disabled={!canSetStartDate}
            />
            <FormSelect
                name={'actionForm.reason'}
                label='Reason For Change'
                control={control}
                rules={{ required: 'Reason for change is required' }}
                options={[
                    { value: 'Volume Growth', displayText: 'Volume Growth' },
                    { value: 'Competition', displayText: 'Competition' },
                    { value: 'Resin Change', displayText: 'Resin Change' },
                    { value: 'Other', displayText: 'Other' },
                ]}
                fieldError={errors?.['actionForm']?.['reason']}
            />
            <FormTextField
                name={'actionForm.comments'}
                label='Reason Notes'
                control={control}
                fieldError={errors?.['actionForm']?.['comments']}
            />
            {InfoBar && <InfoBar startDateFormValue={watch('actionForm.start')} />}
        </MassActionModalLayout>
    );
}

export function useChangeBySetValueMassAction<T>({
    controllerName,
    affectedDataContext,
    selected,
    allRowsSelected,
    onSuccess,
}: UseMassActionProps<T>) {
    const { filtersApplied } = useContext(affectedDataContext);

    const changeBySetValueMapper = (form: ChangeBySetValueForm) => ({
        ...form,
        filters: filtersApplied,
        selected,
        allRowsSelected,
        type: 'Change market price by set value',
    });

    return useSubmitNew({
        name: `${controllerName}/massaction/changebysetvalue`,
        mapper: changeBySetValueMapper,
        context: affectedDataContext,
        onSuccess: onSuccess,
    });
}
